class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('form.m-search-form');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.resultsPanel = this.querySelector('[data-search-results-panel]');
    this.discoveryContent = this.querySelector('[data-search-discovery]');
    this.popularSearches = this.querySelector('[data-popular-searches]');
    this.searchCount = this.querySelector('[data-search-count]');
    this.searchCountWrap = this.querySelector('[data-search-count-wrap]');
    this.searchQuery = this.querySelector('[data-query]');
    this.searchMessage = this.querySelector('[data-message]');
    this.loading = this.querySelector('[data-spinner]');
    this.submit = this.querySelector('button[type="submit"]');
    this.clearButton = this.querySelector('[data-clear-search]');
    this.popup = this.closest('[data-search-popup]');
    this.wrapper = this.closest('[data-search-container]');

    this.cachedResults = {};
    this.searchTimer = null;
    this.abortController = null;
    this.lastRenderedQuery = '';

    this.setupPopularSearches();
    this.setupEventListeners();
    this.updateVisibleViewportHeight();
  }

  setupPopularSearches() {
    this.querySelectorAll('[data-ps-item]').forEach((item) => {
      const query = item.dataset.psQuery || item.textContent.trim();
      if (typeof createSearchLink === 'function') {
        item.href = createSearchLink(query);
      } else {
        item.href = `/search?q=${encodeURIComponent(query)}`;
      }
    });
  }

  setupEventListeners() {
    if (!this.input || !this.form) return;

    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClearSearch.bind(this));
    }

    if (window.MinimogEvents && window.MinimogTheme) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }

    window.addEventListener('resize', () => this.updateVisibleViewportHeight(), { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => this.updateVisibleViewportHeight(), { passive: true });
      window.visualViewport.addEventListener('scroll', () => this.updateVisibleViewportHeight(), { passive: true });
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onInput() {
    const searchTerm = this.getQuery();
    window.clearTimeout(this.searchTimer);

    if (!searchTerm) {
      this.close(true);
      return;
    }

    this.setSearchMode(true);
    this.showLoadingState();

    this.searchTimer = window.setTimeout(() => {
      this.getSearchResults(searchTerm);
    }, 240);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.getQuery()) {
      event.preventDefault();
      this.onClearSearch(event);
    }
  }

onFormSubmit(event) {
  const query = this.getQuery();

  if (!query.length || this.querySelector('[aria-selected="true"] a')) {
    event.preventDefault();
    return;
  }

  const isMobile = window.matchMedia('(max-width: 767px)').matches;

  // The bottom "View all results" button should still go to
  // the full Shopify search results page.
  const isViewAllButton =
    event.submitter &&
    event.submitter.matches('[data-search-count]');

  if (isMobile && !isViewAllButton) {
    event.preventDefault();

    // Close the mobile keyboard.
    if (this.input) {
      this.input.blur();
    }

    // Make sure predictive results remain visible.
    this.setSearchMode(true);

    if (this.resultsPanel) {
      this.resultsPanel.classList.remove('m:hidden');
      this.resultsPanel.scrollTop = 0;
    }

    // If the user submits before the debounce has completed,
    // immediately fetch the current search.
    if (this.lastRenderedQuery !== query) {
      window.clearTimeout(this.searchTimer);
      this.getSearchResults(query);
    }

    this.open();

    // iOS takes a moment to resize visualViewport after
    // the keyboard closes.
    window.setTimeout(() => {
      this.updateVisibleViewportHeight();
    }, 300);

    return;
  }
}
  onClearSearch(event) {
    if (event) event.preventDefault();
    this.close(true);
    this.input.focus();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const searchByTag = this.dataset.searchByTag === 'true';
    const searchByBody = this.dataset.searchByBody === 'true';
    const unavailableProductsOption = this.dataset.unavailableProductsOption || 'last';
    let searchFields = 'title,product_type,vendor,variants.sku,variants.title';

    if (searchByTag) searchFields += ',tag';
    if (searchByBody) searchFields += ',body';

    const searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${encodeURIComponent(
      unavailableProductsOption
    )}&resources[options][fields]=${encodeURIComponent(searchFields)}&section_id=predictive-search`;

    try {
      const response = await fetch(searchURL, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const text = await response.text();
      const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
      const section = parsedDocument.querySelector('#shopify-section-predictive-search');
      const resultsMarkup = section ? section.innerHTML : '';

      this.cachedResults[queryKey] = resultsMarkup;

      if (this.getQuery() === searchTerm) {
        this.renderSearchResults(resultsMarkup, searchTerm);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderErrorState();
      console.error(error);
    }
  }

  showLoadingState() {
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(true);
    this.setSearchMode(true);

    if (this.resultsPanel) {
      this.resultsPanel.classList.remove('m:hidden');
      this.resultsPanel.setAttribute('aria-busy', 'true');
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = `
        <div class="frk-search-loading" role="status" aria-label="Searching">
          <p class="frk-search-loading__label">Searching products and categories…</p>
          <div class="frk-search-loading__row"></div>
          <div class="frk-search-loading__row"></div>
          <div class="frk-search-loading__row"></div>
        </div>
      `;
    }

    this.hideSearchCount();
    this.open();
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.lastRenderedQuery = searchTerm;
    this.setAttribute('results', 'true');

    const searchItemsWrapper = this.querySelector('[data-search-items-wrapper]');
    const hasResults = Boolean(searchItemsWrapper && searchItemsWrapper.children.length);

    this.renderSearchQueryAndMessage(hasResults, searchTerm);
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);

    if (this.resultsPanel) {
      this.resultsPanel.setAttribute('aria-busy', 'false');
      this.resultsPanel.scrollTop = 0;
    }

    this.open();
  }

  renderErrorState() {
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.setSearchMode(true);

    if (this.resultsPanel) {
      this.resultsPanel.classList.remove('m:hidden');
      this.resultsPanel.setAttribute('aria-busy', 'false');
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = `
        <div class="frk-predictive-empty" role="status">
          <h2>Search is taking longer than expected</h2>
          <p>Press the green search button to view the full results page.</p>
        </div>
      `;
    }

    this.showSearchCount(this.getQuery());
  }

  renderSearchQueryAndMessage(hasResults, query) {
    if (this.searchQuery) this.searchQuery.textContent = query;
    if (this.searchMessage) {
      this.searchMessage.textContent = hasResults
        ? this.searchMessage.dataset.resultsTitle || 'View all results for'
        : 'Search the full catalogue for';
    }

    this.showSearchCount(query);
  }

  showSearchCount(query) {
    if (this.searchQuery) this.searchQuery.textContent = query;
    if (this.searchCountWrap) this.searchCountWrap.classList.remove('m:hidden');
    if (this.searchCount) this.searchCount.classList.remove('m:hidden');
  }

  hideSearchCount() {
    if (this.searchCountWrap) this.searchCountWrap.classList.add('m:hidden');
    if (this.searchCount) this.searchCount.classList.add('m:hidden');
  }

  toggleSpinnerLoading(show) {
    if (this.submit) this.submit.style.visibility = show ? 'hidden' : 'visible';
    if (this.loading) this.loading.style.visibility = show ? 'visible' : 'hidden';
  }

  toggleClearSearch(show) {
    if (this.clearButton) this.clearButton.style.visibility = show ? 'visible' : 'hidden';
  }

  setSearchMode(searching) {
    this.classList.toggle('is-searching', searching);
    if (this.popup) this.popup.classList.toggle('is-searching', searching);

    if (this.discoveryContent) {
      this.discoveryContent.classList.toggle('m:hidden', searching);
      this.discoveryContent.setAttribute('aria-hidden', searching ? 'true' : 'false');
    }

    if (this.popularSearches) {
      this.popularSearches.classList.toggle('m:hidden', searching);
    }
  }

  open() {
    this.setSearchMode(true);

    if (this.resultsPanel) {
      this.resultsPanel.classList.remove('m:hidden');
    }

    this.setAttribute('open', 'true');
    if (this.input) this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.updateVisibleViewportHeight();
  }

  close(clearSearchTerm = false) {
    window.clearTimeout(this.searchTimer);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
      this.lastRenderedQuery = '';
    }

    this.removeAttribute('open');
    this.setSearchMode(false);
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(false);
    this.hideSearchCount();

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
    }

    if (this.resultsPanel) {
      this.resultsPanel.classList.add('m:hidden');
      this.resultsPanel.setAttribute('aria-busy', 'false');
      this.resultsPanel.scrollTop = 0;
    }

    if (this.input) this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
  }

  updateVisibleViewportHeight() {
    if (!this.popup) return;

    const viewportHeight = window.visualViewport
      ? Math.round(window.visualViewport.height)
      : window.innerHeight;

    this.popup.style.setProperty('--frk-search-visible-height', `${viewportHeight}px`);
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
